/* eslint-disable @typescript-eslint/no-explicit-any */
import type { QueryKey } from '@tanstack/react-query';
import { EmptyObject } from 'react-hook-form';

export enum DefaultOperationsEnum {
  all = 'all',
  list = 'list',
  allLists = 'allLists',
  detail = 'detail',
  allDetails = 'allDetails',
  notifications = 'notifications',
}

export interface DefaultQueryKeyFactory {
  [DefaultOperationsEnum.all]: () => QueryKey;
}

export interface ListQueryKeyFactory<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ListMethodType extends (...args: any[]) => any = (...args: any[]) => any,
> {
  [DefaultOperationsEnum.allLists]: () => QueryKey;
  [DefaultOperationsEnum.list]: (...listParameters: Parameters<ListMethodType>) => QueryKey;
}

export interface DetailQueryKeyFactory<IdType> {
  [DefaultOperationsEnum.allDetails]: () => QueryKey;
  [DefaultOperationsEnum.detail]: (id: IdType) => QueryKey;
}

export interface NotificationsQueryKeyFactory<IdType> {
  [DefaultOperationsEnum.notifications]: (id: IdType) => QueryKey;
}

export class QueryKeyFactoryGenerator<
  FactoryType extends DefaultQueryKeyFactory = DefaultQueryKeyFactory,
  ExtraMethodsType = EmptyObject,
> {
  private factory: FactoryType & ExtraMethodsType;
  constructor(
    private resource: string,
    extraMethods?: ExtraMethodsType
  ) {
    this.factory = {
      [DefaultOperationsEnum.all]: () => [resource],
      ...(extraMethods || {}),
    } as FactoryType & ExtraMethodsType;
  }

  includeList<
    IdType = number | string,
    ListMethodType extends (args: any) => any = (args: any) => DetailQueryKeyFactory<IdType>,
  >() {
    const extraMethods = {
      ...this.factory,
      [DefaultOperationsEnum.allLists]: () => [...this.factory.all(), DefaultOperationsEnum.list],
      [DefaultOperationsEnum.list]: (...listParameters: Parameters<ListMethodType>) => [
        ...extraMethods[DefaultOperationsEnum.allLists](),
        ...listParameters,
      ],
    };
    return new QueryKeyFactoryGenerator<
      FactoryType & ExtraMethodsType,
      ListQueryKeyFactory<ListMethodType>
    >(this.resource, extraMethods);
  }

  includeDetail<IdType = number | string>() {
    const extraMethods = {
      ...this.factory,
      [DefaultOperationsEnum.allDetails]: () => [
        ...this.factory.all(),
        DefaultOperationsEnum.detail,
      ],
      [DefaultOperationsEnum.detail]: (id: IdType) => [
        ...extraMethods[DefaultOperationsEnum.allDetails](),
        id,
      ],
    };

    return new QueryKeyFactoryGenerator<
      FactoryType & ExtraMethodsType,
      DetailQueryKeyFactory<IdType>
    >(this.resource, extraMethods);
  }

  includeNotifications<IdType = number | string>() {
    const extraMethods = {
      ...this.factory,
      [DefaultOperationsEnum.notifications]: (id: IdType) => [
        ...extraMethods[DefaultOperationsEnum.notifications](id),
        id,
      ],
    };

    return new QueryKeyFactoryGenerator<
      FactoryType & ExtraMethodsType,
      NotificationsQueryKeyFactory<IdType>
    >(this.resource, extraMethods);
  }

  getFactory() {
    return this.factory;
  }

  getDefault< // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ListMethodType extends (...args: any[]) => any = (...args: any[]) => any,
    IdType = number | string,
  >() {
    return this.includeList<ListMethodType>().includeDetail<IdType>().getFactory();
  }
}
